import { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Top sellers' countries on Catawiki (Italian label, ISO 3166-1 alpha-2 code).
const COUNTRIES = [
  { code: "IT", name: "Italia" },
  { code: "FR", name: "Francia" },
  { code: "DE", name: "Germania" },
  { code: "ES", name: "Spagna" },
  { code: "NL", name: "Paesi Bassi" },
  { code: "BE", name: "Belgio" },
  { code: "GB", name: "Regno Unito" },
  { code: "PT", name: "Portogallo" },
  { code: "AT", name: "Austria" },
  { code: "CH", name: "Svizzera" },
  { code: "PL", name: "Polonia" },
  { code: "CZ", name: "Repubblica Ceca" },
  { code: "GR", name: "Grecia" },
  { code: "DK", name: "Danimarca" },
  { code: "SE", name: "Svezia" },
  { code: "NO", name: "Norvegia" },
  { code: "FI", name: "Finlandia" },
  { code: "IE", name: "Irlanda" },
  { code: "HU", name: "Ungheria" },
  { code: "RO", name: "Romania" },
  { code: "BG", name: "Bulgaria" },
  { code: "HR", name: "Croazia" },
  { code: "LT", name: "Lituania" },
  { code: "LV", name: "Lettonia" },
  { code: "EE", name: "Estonia" },
  { code: "TR", name: "Turchia" },
  { code: "US", name: "Stati Uniti" },
  { code: "CA", name: "Canada" },
  { code: "JP", name: "Giappone" },
  { code: "CN", name: "Cina" },
  { code: "HK", name: "Hong Kong" },
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "Nuova Zelanda" },
  { code: "IL", name: "Israele" },
  { code: "AE", name: "Emirati Arabi Uniti" },
  { code: "IN", name: "India" },
  { code: "TH", name: "Thailandia" },
  { code: "ZA", name: "Sudafrica" },
  { code: "BR", name: "Brasile" },
  { code: "MX", name: "Messico" },
  { code: "AR", name: "Argentina" },
];

function CountryChip({ country, onRemove }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 bg-gold/15 border border-gold/40 text-gold text-[11px] uppercase tracking-wider"
      data-testid={`country-chip-${country.code}`}
    >
      <img
        src={`https://flagcdn.com/16x12/${country.code.toLowerCase()}.png`}
        alt=""
        className="w-3 h-2 object-cover"
      />
      {country.name}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); onRemove(country.code); }}
        className="ml-0.5 hover:text-white transition-colors"
        aria-label={`Rimuovi ${country.name}`}
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function CountryRow({ country, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(country.code)}
      className={`w-full flex items-center gap-2.5 px-2 py-1.5 text-sm hover:bg-white/5 transition-colors text-left ${
        selected ? "text-gold" : "text-white/80"
      }`}
      data-testid={`country-option-${country.code}`}
    >
      <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
        selected ? "border-gold bg-gold/20" : "border-white/25"
      }`}>
        {selected && <Check className="w-3 h-3 text-gold" />}
      </div>
      <img
        src={`https://flagcdn.com/16x12/${country.code.toLowerCase()}.png`}
        alt=""
        className="w-4 h-3 object-cover flex-shrink-0"
      />
      <span className="truncate">{country.name}</span>
    </button>
  );
}

export default function CountryMultiSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = value || [];
  const selectedCountries = COUNTRIES.filter((c) => selected.includes(c.code));

  const toggle = (code) => {
    if (selected.includes(code)) {
      onChange(selected.filter((c) => c !== code));
    } else {
      onChange([...selected, code]);
    }
  };
  const remove = (code) => onChange(selected.filter((c) => c !== code));
  const clearAll = () => onChange([]);

  const triggerLabel = selected.length === 0
    ? "Tutti i paesi"
    : `${selected.length} paese${selected.length === 1 ? "" : " (i)"} selezionat${selected.length === 1 ? "o" : "i"}`;

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full bg-transparent border-0 border-b border-white/15 hover:border-white/30 focus:border-gold focus:outline-none px-0 py-2 text-base font-body text-left flex items-center justify-between gap-2 transition-colors"
            data-testid="country-multiselect-trigger"
          >
            <span className={selected.length === 0 ? "text-white/35" : "text-white"}>
              {triggerLabel}
            </span>
            <ChevronDown className="w-4 h-4 text-white/50 flex-shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="bg-surface border-white/10 p-0 w-72 max-h-80 overflow-hidden flex flex-col rounded-none"
          data-testid="country-multiselect-content"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/45">
              Paese venditore
            </span>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="text-[10px] uppercase tracking-wider text-white/50 hover:text-gold transition-colors"
                data-testid="country-clear-all"
              >
                Cancella
              </button>
            )}
          </div>
          <div className="overflow-y-auto py-1">
            {COUNTRIES.map((c) => (
              <CountryRow
                key={c.code}
                country={c}
                selected={selected.includes(c.code)}
                onToggle={toggle}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {selectedCountries.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="country-chips">
          {selectedCountries.map((c) => (
            <CountryChip key={c.code} country={c} onRemove={remove} />
          ))}
        </div>
      )}
    </div>
  );
}
